import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Package, Search, Loader2, CheckCircle2, Clock, XCircle, Truck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_ICON = {
  paid: CheckCircle2,
  pending: Clock,
  failed: XCircle,
  expired: XCircle,
};
const STATUS_COLOR = {
  paid: "text-green-400 border-green-500/30 bg-green-500/10",
  pending: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  failed: "text-red-400 border-red-500/30 bg-red-500/10",
  expired: "text-red-400 border-red-500/30 bg-red-500/10",
};

export default function OrderTracking() {
  const [params] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(params.get("order") || "");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) {
      setError("Veuillez renseigner votre numéro de commande et votre email.");
      return;
    }
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.post("/orders/track", {
        order_number: orderNumber.trim(),
        email: email.trim(),
      });
      setOrder(data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Commande introuvable.");
    } finally {
      setLoading(false);
    }
  };

  const StatusIcon = order ? STATUS_ICON[order.payment_status] || Package : Package;
  const statusColor = order ? STATUS_COLOR[order.payment_status] || "text-stone-300 border-white/10 bg-white/5" : "";

  return (
    <div className="max-w-2xl mx-auto px-4 py-16 sm:py-24" data-testid="order-tracking-page">
      <div className="text-center mb-10">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/10 border border-amber-500/30 mb-4">
          <Truck className="h-7 w-7 text-amber-400" />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-stone-50">Suivi de commande</h1>
        <p className="text-stone-400 mt-3">
          Saisissez votre numéro de commande (ex. CM-123456) et l'email utilisé lors de l'achat.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4 bg-[#120E1A] border border-amber-900/20 rounded-2xl p-6 sm:p-8">
        <div>
          <label className="block text-sm font-semibold text-stone-200 mb-2">Numéro de commande</label>
          <input
            data-testid="track-order-number"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder="CM-123456"
            className="w-full rounded-xl bg-[#08060B] border border-white/10 px-4 py-3 text-stone-100 outline-none focus:border-amber-500 transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-stone-200 mb-2">Email de la commande</label>
          <input
            data-testid="track-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@exemple.fr"
            className="w-full rounded-xl bg-[#08060B] border border-white/10 px-4 py-3 text-stone-100 outline-none focus:border-amber-500 transition-colors"
          />
        </div>
        {error && (
          <p data-testid="track-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="track-submit"
          className="btn-ember w-full rounded-xl px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
          {loading ? "Recherche…" : "Suivre ma commande"}
        </button>
      </form>

      {order && (
        <div className="mt-8 bg-[#120E1A] border border-amber-900/20 rounded-2xl p-6 sm:p-8" data-testid="track-result">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div>
              <p className="eyebrow text-stone-500">Commande</p>
              <p className="font-mono-tech text-2xl font-black text-amber-400" data-testid="track-result-number">
                {order.order_number}
              </p>
            </div>
            <span className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold ${statusColor}`} data-testid="track-result-status">
              <StatusIcon className="h-4 w-4" />
              {order.status_info?.label}
            </span>
          </div>

          <p className="text-stone-300 mb-6">{order.status_info?.message}</p>

          {order.tracking_number && (
            <div className="mb-6 rounded-xl bg-amber-500/5 border border-amber-500/20 px-4 py-3" data-testid="track-result-tracking">
              <p className="text-xs text-stone-500">Numéro de suivi</p>
              <p className="font-mono-tech text-amber-400 font-bold">{order.tracking_number}</p>
            </div>
          )}

          <div className="border-t border-amber-900/20 pt-5 space-y-3">
            {order.items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-stone-300">
                  {it.name} <span className="text-stone-500">× {it.quantity}</span>
                </span>
                {it.amount && <span className="font-mono-tech text-stone-200">{it.amount}</span>}
              </div>
            ))}
          </div>

          <div className="border-t border-amber-900/20 mt-5 pt-5 space-y-2">
            <div className="flex justify-between text-sm text-stone-400">
              <span>Livraison</span>
              <span className="font-mono-tech">{order.shipping}</span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="font-bold text-stone-100">Total</span>
              <span className="font-mono-tech text-2xl font-extrabold text-amber-400" data-testid="track-result-total">
                {order.total}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
